import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { EMPTY, from } from 'rxjs'
import { catchError, switchMap, withLatestFrom, mergeMap, count, filter } from 'rxjs/operators'
import { Action, Store, select } from '@ngrx/store'
import { DateTime } from 'luxon'

import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { PayloadedAction } from '../../shared/types'
import { AAAStore } from '../../store/root-reducer'
import { AppointmentService } from './appointment.service'
import { AppointmentAdapter } from '../../shared/adapters/AppointmentAdapter'
import {
  APPOINTMENT_DATE,
  APPOINTMENT_AVAILABILITY,
  appointmentDateFailure,
  appointmentDateSuccess,
  appointmentAvailabilityFailure,
  requestAppointmentDate,
  requestAppointmentAvailability
} from './appointment.actions'
import { AppointmentRequest, AppointmentAvailabilityRequest, AppointmentAvailability, AppointmentResponse, AppointmentDate } from './appointment.types'
import { selectAppointmentAvailability, selectAppointmentEnabled } from './appointment.selectors'
import { selectLocationClub } from '../location/location.selectors'
import { SET_PACE_SETTER_SITUATION, PACE_SETTER_NEXT } from '../issue/issue.actions'
import { selectBreakdownLocation } from '../location/location.selectors'
import { BreakdownLocation } from '../location/location.types'

@Injectable()
export class AppointmentEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private appointmentService: AppointmentService,
    private appointmentAdapter: AppointmentAdapter,
    private errorReportingService: ErrorReportingService
  ) {}

  maxDays = 3

  requestAppointmentSchedule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_PACE_SETTER_SITUATION, PACE_SETTER_NEXT),
      withLatestFrom(
        this.store$.pipe(select(selectAppointmentEnabled)),
        this.store$.pipe(select(selectBreakdownLocation))
      ),
      filter(([_, enabled]) => enabled),
      switchMap(([_, _2, breakdownLocation]: [Action, boolean, BreakdownLocation]) => [
        requestAppointmentAvailability({
          payload: {
            latitude: Number(breakdownLocation.latitude),
            longitude: Number(breakdownLocation.longitude),
            date: DateTime.now().toJSDate(),
            days: this.maxDays,
          },
        })
      ])
    )
  )

  requestAppointmentAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestAppointmentAvailability>>(APPOINTMENT_AVAILABILITY.REQUEST),
      withLatestFrom(
        this.store$.pipe(select(selectAppointmentAvailability))
      ),
      switchMap(([action, availability]: [PayloadedAction<AppointmentAvailabilityRequest>, AppointmentAvailability]) => {
        const currentDatesWithSlots = availability?.dates?.filter(date => date.slots.length > 0) || []
        const days = action.payload.days || 1

        if (currentDatesWithSlots.length >= days) {
          // If we have enough slots, return the success action and stop the recursion
          return EMPTY
        }

        const datesNeeded = days - currentDatesWithSlots.length
        const nextDates = this.calculateNextDates(action.payload.date, availability?.dates || [], datesNeeded)

        // Create appointment requests (concurrency controlled in requestAppointmentDate$ effect)
        const appointmentRequests = nextDates.map(date =>
          requestAppointmentDate({
            payload: {
              ...action.payload,
              date
            }
          })
        )

        return appointmentRequests
      }),
      catchError((error) =>
        this.errorReportingService.notifyErrorObservable(
          error,
          appointmentAvailabilityFailure
        )
      )
    )
  )

  requestAppointmentDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof requestAppointmentAvailability>>(APPOINTMENT_DATE.REQUEST),
      withLatestFrom(
        this.store$.pipe(select(selectLocationClub))
      ),
      mergeMap(([action, locationClub]: [PayloadedAction<AppointmentRequest>, string]) =>
        from(this.appointmentService.getAppointmentTimes(locationClub, action.payload)).pipe(
          mergeMap((response: AppointmentResponse) => {
            const actions: Action[] = []
            const adaptedData = this.appointmentAdapter.adapt({
              ...response,
              ...action.payload,
            })

            if (adaptedData.slots.length > 0) {
              actions.push(appointmentDateSuccess({ payload: adaptedData, meta: { cacheKey: locationClub } }))
            }
            //Recursively request more dates if we don't have enough slots
            actions.push(requestAppointmentAvailability({ payload: action.payload }))
            return actions
          }),
          catchError((error) =>
            this.errorReportingService.notifyErrorObservable(
              error,
              appointmentDateFailure
            )
          )
        ),
      this.maxDays
      )
    )
  )

  calculateNextDates(startDate: Date, existingDates: AppointmentDate[], count: number): Date[] {
    const existingTimestamps = new Set(
      existingDates.map(d => DateTime.fromJSDate(d.date).toISODate())
    );

    const result: Date[] = [];
    let current = DateTime.fromJSDate(startDate).plus({ days: 1 });

    while (result.length < count) {
      const isoDate = current.toISODate(); // 'yyyy-MM-dd'

      if (!existingTimestamps.has(isoDate)) {
        result.push(current.toJSDate());
      }

      current = current.plus({ days: 1 });
    }

    return result;
  }
}
